import { Alert } from "@gigpro/ui/Alert";
import { createColumnHelper, DataTable } from "@gigpro/ui/DataTable";
import { formatCurrency } from "@gigpro/utils";
import { formatDate } from "@gigpro/utils/datetime";
import type { AdminSickTimeSummary, AdminSickTimeUsage } from "@sidegigllc/api/client";
import { useQuery } from "@tanstack/react-query";
import { compareDesc } from "date-fns";
import { CopyButton } from "domains/admin/components/CopyButton";
import { sickTimeQuery } from "./queries";

const formatHours = (hours: number) => `${hours.toFixed(2)} h`;
const formatRate = (rate: number | null) => (rate == null ? "—" : `${formatCurrency(rate)}/hr`);

const columnHelper = createColumnHelper<AdminSickTimeUsage>();
const columnDefaults = { enableSorting: false, enableColumnFilter: false } as const;

const columns = [
  columnHelper.accessor("work_date", {
    ...columnDefaults,
    header: "Date",
    cell: ({ getValue }) => formatDate(getValue(), "MM/dd/yyyy"),
  }),
  columnHelper.accessor("hours", {
    ...columnDefaults,
    header: "Hours",
    cell: ({ getValue }) => formatHours(getValue()),
  }),
  columnHelper.accessor("regular_rate", {
    ...columnDefaults,
    header: "Rate",
    cell: ({ getValue }) => formatRate(getValue()),
  }),
  columnHelper.accessor("amount", {
    ...columnDefaults,
    header: "Amount",
    cell: ({ getValue }) => formatCurrency(getValue()),
  }),
  columnHelper.accessor("employee_check_id", {
    ...columnDefaults,
    header: "Zeal check id",
    cell: ({ getValue }) => {
      const checkId = getValue();
      return checkId ? <CopyButton copyValue={checkId} title="Copy Zeal check id" /> : "—";
    },
  }),
  columnHelper.accessor("status", { ...columnDefaults, header: "Status" }),
];

// Newest request first by `created_at`; two requests can share a `work_date`.
const newestFirst = (a: AdminSickTimeUsage, b: AdminSickTimeUsage) => compareDesc(a.created_at, b.created_at);

export function SickTime({ proId }: Readonly<{ proId: number }>) {
  const sickTime = useQuery(sickTimeQuery({ proId }));

  if (sickTime.isPending) return <p className="p-6 text-gray">Loading...</p>;
  if (sickTime.isError)
    return <p className="p-6 text-gray">Couldn't load this pro's sick time. Refresh to try again.</p>;
  if (!sickTime.data.is_w2) return <p className="p-6 text-gray">Sick time only applies to W-2 pros.</p>;

  const { summary, summary_error, usage } = sickTime.data;
  const rows = usage.toSorted(newestFirst);

  return (
    <div className="flex flex-col gap-2 py-2">
      {summary_error && <Alert severity="warning">Couldn't load balance from Zeal</Alert>}
      {summary && <SummaryCard summary={summary} />}

      <section className="rounded-sm bg-white py-3 shadow-down">
        <h2 className="mb-3 ml-4 text-extraLarge font-semibold">Sick time used</h2>
        {rows.length === 0 ? (
          <p className="px-4 text-gray">No sick time used</p>
        ) : (
          <DataTable hideToolbar columns={columns} data={rows} />
        )}
      </section>
    </div>
  );
}

function SummaryCard({ summary }: Readonly<{ summary: AdminSickTimeSummary }>) {
  const stats = [
    { label: "Available", value: formatHours(summary.available_hours) },
    { label: "Accrued", value: formatHours(summary.balance_hours) },
    { label: "Pending", value: formatHours(summary.pending_hours) },
    { label: "Regular rate", value: formatRate(summary.regular_rate) },
    { label: "Zeal policy code", value: summary.policy_code },
  ];

  return (
    <section aria-label="Sick time balance" className="rounded-sm bg-white px-4 py-3 shadow-down">
      <dl className="flex flex-wrap gap-x-8 gap-y-2">
        {stats.map((stat) => (
          <div key={stat.label} className="flex flex-col">
            <dt className="text-small text-gray">{stat.label}</dt>
            <dd className="text-large font-semibold">{stat.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
